/**
 * Shop Tracker – DOM-Tracking im Theme.
 *
 * Deckt ab, was der Custom Pixel prinzipiell nicht sehen kann (er läuft
 * sandboxed, ohne DOM): Verweildauer, Scroll-Tiefe, Aufmerksamkeit pro
 * Seitenabschnitt, Klicks samt Position für die Heatmap, Reibungssignale
 * sowie die kaufvorbereitenden Interaktionen — Variante gewählt, Filter
 * gesetzt, Kachel auf Position 7 geklickt, Popup weggeklickt.
 *
 * Läuft ausschließlich nach Consent über die Shopify Customer Privacy API.
 * Config kommt aus window.__TRACKER_CONFIG__ (siehe snippet.liquid).
 *
 * Grundsatz: Tracking darf den Shop nie beeinträchtigen. Jeder Handler ist
 * gekapselt, jede optionale Browser-API wird geprüft, nichts wirft nach außen.
 */
(function () {
  "use strict";

  var config = window.__TRACKER_CONFIG__ || {};
  var COLLECT_URL = config.collectUrl;

  if (!COLLECT_URL) {
    console.warn("[tracker] fehlende Config (collectUrl), breche ab");
    return;
  }

  var consentGiven = false;
  var sessionId = null;
  var visitorId = null;
  var queue = [];
  var flushTimer = null;

  var IDLE_TIMEOUT_MS = 30000;
  var HEARTBEAT_MS = 10000;
  var ATTENTION_TICK_MS = 1000;
  var FLUSH_INTERVAL_MS = 5000;
  var FLUSH_BATCH_SIZE = 10;
  // Obergrenze pro Seitenaufruf. Schützt gegen Endlosschleifen in fremdem
  // Theme-Code, die sonst tausende Events erzeugen könnten.
  var MAX_EVENTS_PER_PAGE = 300;

  /**
   * Automatisierten Aufruf erkennen. Absichtlich nur Signale, die eindeutig
   * sind — im Zweifel gilt ein Besuch als echt, denn eine fälschlich als Bot
   * markierte Sitzung verschwindet aus jeder Auswertung.
   *
   * Das Ergebnis geht als `bot: true` mit; ausgeblendet wird serverseitig
   * (migrations/008_quality_filter.sql). Der Tracker bricht bewusst NICHT ab:
   * sonst wäre nirgends ablesbar, wie viel automatisierter Verkehr anfällt.
   * `collect` prüft zusätzlich den User-Agent-Header; die Client-Prüfung
   * ergänzt das um das, was nur im Browser sichtbar ist.
   */
  function detectBot() {
    try {
      if (navigator.webdriver === true) return true;
      // Kopflose Chrome-Varianten geben sich im User-Agent zu erkennen.
      if (/HeadlessChrome|PhantomJS|Puppeteer|Playwright|Selenium/i.test(navigator.userAgent)) {
        return true;
      }
      // Ein echter Browser meldet mindestens eine Sprache.
      if (Array.isArray(navigator.languages) && navigator.languages.length === 0) return true;
      return false;
    } catch (e) {
      return false;
    }
  }

  var isBot = detectBot();

  var eventsSent = 0;
  var lastActivityAt = Date.now();
  var dwellMs = 0;
  var activeMs = 0;
  // Zeitstempel statt Zähler: die Verweildauer wird als tatsächlich
  // vergangene Zeit gemessen, nicht in Vielfachen des Heartbeats
  // aufaddiert — sonst meldet ein Besuch unter 10 Sekunden schlicht nichts.
  var lastTickAt = Date.now();
  var wasVisible = document.visibilityState === "visible";
  var heartbeatInterval = null;
  var attentionInterval = null;

  /**
   * Theme-abhängige Selektoren an einer Stelle. Die Voreinstellungen decken
   * Dawn und die davon abgeleiteten Themes ab; abweichende Themes können sie
   * über window.__TRACKER_CONFIG__.selectors einzeln überschreiben, ohne
   * diese Datei anzufassen.
   */
  var SEL = {
    productCard:
      "[data-product-id], .card-wrapper, .product-card, .product-item, .grid__item",
    addToCart:
      '[name="add"], .product-form__submit, [data-add-to-cart], form[action*="/cart/add"] button[type="submit"]',
    variantInput:
      ".product-form__input input[type=radio], .product-form__input select, " +
      "variant-radios input[type=radio], variant-selects select, [data-option-value]",
    // Varianten, die als Button/Link umgesetzt sind statt als Formularfeld.
    // Solche Umsetzungen laden meist direkt die nächste Produkt-URL — sie
    // erzeugen also nie ein `change`-Event, das variantInput sehen könnte.
    variantClick: "[data-variant-url], .custom-variant-btn",
    quantityInput: 'input[name="quantity"], quantity-input input',
    sortSelect: '[name="sort_by"], #SortBy',
    filterScope: "#FacetFiltersForm, facet-filters-form, .facets, [data-filter-form]",
    media:
      ".product__media, .product-media, [data-media-id], .thumbnail, " +
      ".product__thumbnail, [data-zoom], .product-single__thumbnail",
    popup:
      '[class*="newsletter" i], [class*="popup" i], [id*="popup" i], ' +
      '[class*="modal" i][class*="subscribe" i], dialog[open]',
    popupClose:
      '[class*="close" i], [aria-label*="close" i], [aria-label*="schließen" i], [data-close]'
  };
  if (config.selectors) {
    for (var selKey in config.selectors) {
      if (config.selectors[selKey]) SEL[selKey] = config.selectors[selKey];
    }
  }

  /** Handler-Kapsel: ein Fehler in einem Listener darf nie den Shop treffen. */
  function safe(fn) {
    return function (arg) {
      try {
        fn(arg);
      } catch (e) {
        /* stumm */
      }
    };
  }

  function on(target, type, fn, opts) {
    try {
      target.addEventListener(type, safe(fn), opts);
    } catch (e) {
      /* Event-Typ vom Browser nicht unterstützt */
    }
  }

  function textOf(el, max) {
    if (!el) return null;
    var t = (el.textContent || "").trim().replace(/\s+/g, " ");
    return t ? t.slice(0, max || 80) : null;
  }

  // ─────────────────────────────────────────────────────────────
  // Identität & Basisfelder
  // ─────────────────────────────────────────────────────────────

  function hasAnalyticsConsent() {
    try {
      return !!(
        window.Shopify &&
        window.Shopify.customerPrivacy &&
        window.Shopify.customerPrivacy.analyticsProcessingAllowed()
      );
    } catch (e) {
      return false;
    }
  }

  function getSessionId() {
    var KEY = "__tracker_session_id";
    var TS_KEY = "__tracker_session_ts";
    var now = Date.now();
    var stored = sessionStorage.getItem(KEY);
    var ts = parseInt(sessionStorage.getItem(TS_KEY) || "0", 10);

    if (!stored || now - ts > 30 * 60 * 1000) {
      stored = "s_" + now.toString(36) + "_" + Math.random().toString(36).slice(2, 10);
    }
    sessionStorage.setItem(KEY, stored);
    sessionStorage.setItem(TS_KEY, String(now));
    return stored;
  }

  function getVisitorId() {
    var KEY = "__tracker_visitor_id";
    var stored = localStorage.getItem(KEY);
    if (!stored) {
      stored = "v_" + Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 10);
      localStorage.setItem(KEY, stored);
    }
    return stored;
  }

  function detectDevice() {
    return /Mobi|Android/i.test(navigator.userAgent) ? "mobile" : "desktop";
  }

  // Muss dieselben Werte liefern wie pageTypeOf() in pixel/custom-pixel.js —
  // sonst zerfällt derselbe Seitentyp in den Views in zwei Gruppen.
  function detectPageType() {
    if (config.pageType) return config.pageType;
    var path = location.pathname;
    if (path === "/") return "home";
    if (path.indexOf("/collections/") === 0) return "collection";
    if (path.indexOf("/products/") === 0) return "product";
    if (path.indexOf("/cart") === 0) return "cart";
    if (path.indexOf("/checkouts") === 0 || path.indexOf("/checkout") === 0) return "checkout";
    if (path.indexOf("/search") === 0) return "search";
    if (path.indexOf("/blogs/") === 0) return "article";
    if (path.indexOf("/account") === 0) return "account";
    if (path.indexOf("/pages/") === 0) return "page";
    return "other";
  }

  function baseFields() {
    return {
      // shop nur als Rückfallebene: normalerweise erkennt collect den Shop am
      // Origin-Header. Bewusst location.hostname und NICHT config.shop — das
      // ist shop.permanent_domain (…myshopify.com) und stünde weder in
      // ALLOWED_SHOPS noch würde es zum shop-Wert der Pixel-Events passen,
      // was Sitzungen in v_sessionized auf zwei Shops aufspalten würde.
      shop: location.hostname,
      source: "theme",
      session_id: sessionId,
      visitor_id: visitorId,
      path: location.pathname,
      page_type: detectPageType(),
      referrer: document.referrer || null,
      device: detectDevice(),
      viewport: window.innerWidth + "x" + window.innerHeight,
      language: navigator.language || null,
      // Nur setzen, wenn erkannt: `false` wäre eine Behauptung, die der Server
      // ohnehin nicht übernimmt (siehe detectBot in collect/index.ts).
      bot: isBot || undefined
    };
  }

  /**
   * Produktkontext der aktuellen Seite. Kommt aus Liquid (siehe snippet.liquid)
   * und ist deshalb zuverlässiger als alles, was sich aus dem DOM raten ließe.
   * Wird bewusst NUR an produktbezogene Events gehängt: an jedem Event würde es
   * v_product_funnel mit Produkten fluten, die nur angeklickt, aber nie
   * angesehen wurden.
   */
  function productContext() {
    var ctx = {};
    if (config.productId) ctx.product_id = config.productId;
    if (config.productHandle) ctx.product_handle = config.productHandle;
    if (config.productTitle) ctx.product_title = config.productTitle;
    return ctx;
  }

  // ─────────────────────────────────────────────────────────────
  // Versand
  // ─────────────────────────────────────────────────────────────

  function enqueue(event) {
    if (!consentGiven) return;
    if (eventsSent >= MAX_EVENTS_PER_PAGE) return;
    eventsSent++;

    // Zeitpunkt des Geschehens, nicht des Sendens. Bis zu FLUSH_BATCH_SIZE
    // Events reisen gemeinsam, und `events.ts` in der Datenbank ist der
    // Empfang — ohne diese Zeile hätten alle Events eines Bündels denselben
    // Zeitstempel, und die Abstände im Sitzungsverlauf wären erfunden.
    //
    // Absolut ist der Wert nur so verlässlich wie die Uhr des Besuchers;
    // ausgewertet werden deshalb ausschließlich Differenzen innerhalb einer
    // Sitzung (siehe Migration 011). Sitzungsgrenzen rechnen weiter mit `ts`.
    if (event.client_ts == null) event.client_ts = Date.now();

    queue.push(event);
    if (queue.length >= FLUSH_BATCH_SIZE) flush();
  }

  /** send(type, extra) — Basisfelder + typspezifische Ergänzungen. */
  function send(type, extra) {
    var event = Object.assign({ type: type }, baseFields());
    if (extra) {
      for (var key in extra) {
        if (extra[key] != null) event[key] = extra[key];
      }
    }
    enqueue(event);
  }

  function flush(useBeacon) {
    if (queue.length === 0) return;
    var batch = queue;
    queue = [];
    var body = JSON.stringify(batch);

    if (useBeacon && navigator.sendBeacon) {
      var blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(COLLECT_URL, blob);
    } else {
      fetch(COLLECT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: body,
        keepalive: true
      }).catch(function () {});
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Verweildauer (brutto / aktiv)
  // ─────────────────────────────────────────────────────────────

  function markActivity() {
    lastActivityAt = Date.now();
  }

  function isIdle() {
    return Date.now() - lastActivityAt > IDLE_TIMEOUT_MS;
  }

  /**
   * Schreibt die seit dem letzten Aufruf vergangene Zeit fort.
   *
   * Zwei Dinge, die die frühere Zähler-Variante (dwellMs += HEARTBEAT_MS)
   * nicht konnte und die beide für den Qualitätsfilter zählen:
   * 1. Der angebrochene letzte Abschnitt geht nicht verloren. Vorher meldete
   *    ein Besuch von 9 Sekunden `dwell_ms = 0` und damit gar kein
   *    dwell-Event — ausgerechnet die kurzen Besuche blieben unmessbar.
   * 2. Ein gedrosselter Timer (Hintergrund-Tab, mobiles Energiesparen)
   *    verfälscht die Dauer nicht mehr nach unten.
   *
   * `wasVisible` statt document.visibilityState: beim visibilitychange steht
   * der neue Zustand schon fest, die gerade abgelaufene Zeit gehört aber noch
   * zum alten.
   */
  function accumulate() {
    var now = Date.now();
    var elapsed = now - lastTickAt;
    lastTickAt = now;
    if (elapsed <= 0 || !wasVisible) return;
    dwellMs += elapsed;
    if (!isIdle()) activeMs += elapsed;
  }

  function heartbeat() {
    accumulate();
  }

  function flushDwell() {
    accumulate();
    if (dwellMs <= 0) return;
    send("dwell", { dwell_ms: dwellMs, active_ms: activeMs });
    dwellMs = 0;
    activeMs = 0;
  }

  // ─────────────────────────────────────────────────────────────
  // Scroll-Tiefe
  // ─────────────────────────────────────────────────────────────
  // Gemessen wird die maximal erreichte Tiefe der UNTERKANTE des Viewports,
  // nicht die der Oberkante: wer eine kurze Seite ohne Scrollen komplett sieht,
  // hat 100 % gesehen, nicht 0 %.

  var maxScrollPct = 0;
  var scrollStartedAt = Date.now();
  var timeToMaxMs = 0;

  function docHeight() {
    var b = document.body;
    var d = document.documentElement;
    return Math.max(
      b ? b.scrollHeight : 0,
      b ? b.offsetHeight : 0,
      d ? d.scrollHeight : 0,
      d ? d.offsetHeight : 0,
      d ? d.clientHeight : 0
    );
  }

  function currentScrollPct() {
    var h = docHeight();
    if (!h) return 0;
    var bottom = (window.scrollY || window.pageYOffset || 0) + window.innerHeight;
    return Math.max(0, Math.min(100, Math.round((bottom / h) * 100)));
  }

  function trackScrollPosition() {
    var pct = currentScrollPct();
    if (pct > maxScrollPct) {
      maxScrollPct = pct;
      timeToMaxMs = Date.now() - scrollStartedAt;
    }
  }

  function flushScroll() {
    trackScrollPosition();
    if (maxScrollPct <= 0) return;
    send("scroll", {
      scroll_pct: maxScrollPct,
      payload: { doc_height: docHeight(), time_to_max_ms: timeToMaxMs }
    });
    maxScrollPct = 0;
  }

  // ─────────────────────────────────────────────────────────────
  // Aufmerksamkeit pro 10-%-Band
  // ─────────────────────────────────────────────────────────────
  // Jede Sekunde wird gezählt, welche Zehntel der Seite gerade im Viewport
  // liegen. Bewusst OHNE IntersectionObserver: der bräuchte zehn eingefügte
  // Hilfselemente im fremden Theme-Layout. Die Rechnung hier kommt ohne
  // DOM-Eingriff aus und liefert dasselbe Ergebnis.
  //
  // Gezählt wird nur, was sichtbar UND nicht idle ist — ein offener
  // Hintergrund-Tab oder eine Seite, vor der niemand sitzt, ist keine
  // Aufmerksamkeit.

  var bands = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

  function attentionTick() {
    if (document.visibilityState !== "visible" || isIdle()) return;
    var h = docHeight();
    if (!h) return;

    var top = window.scrollY || window.pageYOffset || 0;
    var bottom = top + window.innerHeight;
    var first = Math.max(0, Math.min(9, Math.floor((top / h) * 10)));
    var last = Math.max(0, Math.min(9, Math.floor((bottom / h) * 10)));

    for (var i = first; i <= last; i++) bands[i] += ATTENTION_TICK_MS;
  }

  function flushAttention() {
    var any = bands.some(function (v) { return v > 0; });
    if (!any) return;
    send("attention", { payload: { bands: bands.slice() } });
    bands = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
  }

  // ─────────────────────────────────────────────────────────────
  // Klicks — Label, Selektor, Position, Rage & Dead
  // ─────────────────────────────────────────────────────────────

  var INTERACTIVE = "a, button, [data-track], input, select, textarea, label, [role=button]";

  function resolveTargetLabel(el) {
    var explicit = el.getAttribute("data-track");
    if (explicit) return explicit;
    var aria = el.getAttribute("aria-label");
    if (aria) return aria.slice(0, 80);
    return textOf(el, 80);
  }

  function resolveSelector(el) {
    var parts = [];
    var node = el;
    var depth = 0;
    while (node && node.nodeType === 1 && depth < 5) {
      var part = node.tagName.toLowerCase();
      if (node.id) {
        part += "#" + node.id;
        parts.unshift(part);
        break;
      } else if (node.className && typeof node.className === "string") {
        part += "." + node.className.trim().split(/\s+/).slice(0, 2).join(".");
      }
      parts.unshift(part);
      node = node.parentElement;
      depth++;
    }
    return parts.join(">").slice(0, 256);
  }

  /**
   * Klickposition in Prozent statt Pixeln: x relativ zur Viewport-Breite,
   * y relativ zur gesamten Dokumenthöhe. Nur so sind Klicks über
   * verschiedene Bildschirmgrößen hinweg überhaupt vergleichbar — die
   * Auswertung trennt zusätzlich nach Gerät (siehe migrations/007).
   */
  function clickPosition(e) {
    var pos = {};
    var w = window.innerWidth;
    var h = docHeight();
    if (w > 0 && typeof e.clientX === "number") {
      pos.click_x_pct = Math.max(0, Math.min(100, Math.round((e.clientX / w) * 100)));
    }
    if (h > 0 && typeof e.clientY === "number") {
      var absY = e.clientY + (window.scrollY || window.pageYOffset || 0);
      pos.click_y_pct = Math.max(0, Math.min(100, Math.round((absY / h) * 100)));

      // Zusätzlich absolut, in Pixeln vom Seitenanfang.
      //
      // Prozent sind über verschiedene Bildschirmbreiten vergleichbar und
      // bleiben deshalb die Grundlage jeder Aggregation. Sie haben aber einen
      // blinden Fleck: der Nenner ist die Dokumenthöhe, und die ändert sich
      // im Laufe eines Besuchs — ein aufgeklapptes Akkordeon, ein nachgeladener
      // Abschnitt, ein eingeblendetes Popup. Derselbe Button liegt dann bei
      // 46 % statt bei 41 %, ohne sich bewegt zu haben.
      //
      // Für die Darstellung über einem Screenshot ist genau das der Fehler:
      // dort ist die Höhe fest. Mit der absoluten Position und der Höhe, die
      // der Besucher gesehen hat, lässt sich der Punkt richtig einsortieren.
      pos.click_y_px = Math.min(200000, Math.round(absY));
      pos.doc_h_px = Math.min(200000, Math.round(h));
    }
    return pos;
  }

  // Rage-Click: mehrere Klicks kurz hintereinander an fast derselben Stelle.
  var RAGE_WINDOW_MS = 800;
  var RAGE_RADIUS_PX = 40;
  var RAGE_THRESHOLD = 3;
  var recentClicks = [];

  function checkRage(e, el) {
    var now = Date.now();
    recentClicks = recentClicks.filter(function (c) { return now - c.t < RAGE_WINDOW_MS; });
    recentClicks.push({ t: now, x: e.clientX, y: e.clientY });

    var near = recentClicks.filter(function (c) {
      return Math.abs(c.x - e.clientX) < RAGE_RADIUS_PX &&
             Math.abs(c.y - e.clientY) < RAGE_RADIUS_PX;
    });

    if (near.length >= RAGE_THRESHOLD) {
      recentClicks = [];
      send("rage_click", Object.assign({
        target_label: resolveTargetLabel(el),
        target_selector: resolveSelector(el),
        payload: { click_count: near.length }
      }, clickPosition(e)));
    }
  }

  /**
   * Dead-Click: etwas sieht klickbar aus (cursor: pointer), ist aber kein
   * echtes Bedienelement, und der Klick löst innerhalb einer Sekunde keinerlei
   * DOM-Änderung aus. Genau das ist die Situation, in der ein Kunde denkt, der
   * Shop sei kaputt.
   *
   * Der MutationObserver wird nur für dieses eine Zeitfenster angelegt und
   * danach sofort wieder getrennt — dauerhaft auf dem ganzen Dokument zu
   * lauschen wäre auf einer Shop-Seite zu teuer.
   */
  var deadClickPending = false;

  function checkDeadClick(e, el) {
    if (deadClickPending) return;
    if (el.closest(INTERACTIVE)) return;
    if (typeof MutationObserver !== "function") return;

    var looksClickable = false;
    try {
      looksClickable = window.getComputedStyle(el).cursor === "pointer";
    } catch (err) {
      return;
    }
    if (!looksClickable) return;

    deadClickPending = true;
    var changed = false;
    var observer = new MutationObserver(function () { changed = true; });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });

    var label = resolveTargetLabel(el);
    var selector = resolveSelector(el);
    var pos = clickPosition(e);

    setTimeout(safe(function () {
      observer.disconnect();
      deadClickPending = false;
      if (!changed) {
        send("dead_click", Object.assign({
          target_label: label,
          target_selector: selector
        }, pos));
      }
    }), 900);
  }

  // ─────────────────────────────────────────────────────────────
  // Produktlisten: welche Kachel auf welcher Position
  // ─────────────────────────────────────────────────────────────

  function listNameFor(pageType) {
    if (pageType === "collection") return "collection";
    if (pageType === "search") return "search";
    if (pageType === "home") return "home";
    if (pageType === "product") return "recommendations";
    return pageType || "other";
  }

  function handleFromHref(href) {
    if (!href) return null;
    var m = String(href).match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]).slice(0, 128) : null;
  }

  function trackListClick(el) {
    var card = el.closest(SEL.productCard);
    if (!card) return false;

    var link = card.querySelector('a[href*="/products/"]') ||
               (el.closest('a[href*="/products/"]'));
    var handle = handleFromHref(link && link.getAttribute("href"));
    if (!handle) return false;

    // Position in der sichtbaren Reihenfolge. Dokumentreihenfolge entspricht
    // bei Grid-Layouts der Leserichtung — genau das, was interessiert.
    var position = null;
    try {
      var all = document.querySelectorAll(SEL.productCard);
      for (var i = 0; i < all.length; i++) {
        if (all[i] === card) { position = i + 1; break; }
      }
    } catch (e) {
      /* Selektor vom Theme überschrieben und ungültig */
    }

    send("product_list_click", {
      product_handle: handle,
      product_id: card.getAttribute("data-product-id") || null,
      product_title: textOf(card.querySelector("a[href*='/products/']"), 200) || null,
      list_name: listNameFor(detectPageType()),
      list_position: position,
      target_label: handle,
      target_selector: resolveSelector(card)
    });
    return true;
  }

  // ─────────────────────────────────────────────────────────────
  // Zentrale Klickverarbeitung
  // ─────────────────────────────────────────────────────────────

  function onClickCapture(e) {
    var target = e.target;
    if (!target || !target.closest) return;

    checkRage(e, target);

    var el = target.closest(INTERACTIVE);
    if (!el) {
      checkDeadClick(e, target);
      return;
    }

    // Wird gesetzt, wenn der Klick die Seite verlässt: dann muss die Warte-
    // schlange sofort per Beacon raus, sonst geht sie beim Navigieren verloren.
    var leavesPage = false;

    // Variante als Button/Link (statt als Formularfeld). Läuft über den Klick,
    // weil solche Umsetzungen kein `change` auslösen.
    var variantBtn = el.closest(SEL.variantClick);
    if (variantBtn) {
      leavesPage = true;
      send("variant_selected", Object.assign({
        target_label: resolveTargetLabel(variantBtn),
        payload: {
          option_name: variantBtn.getAttribute("data-option-name") || "Variante",
          option_value: resolveTargetLabel(variantBtn) || "–"
        }
      }, productContext()));
    }

    // Klick auf eine Produktkachel wird als eigener Typ gemeldet — mit
    // Position und Handle, nicht nur als anonymer Klick.
    trackListClick(el);

    // "In den Warenkorb": das Theme sieht den Klick, der Pixel erst den
    // Erfolg. Die Differenz beider Zahlen ist das eigentliche Signal.
    if (el.closest(SEL.addToCart)) {
      send("add_to_cart_clicked", Object.assign(
        { target_label: resolveTargetLabel(el), target_selector: resolveSelector(el) },
        productContext(),
        clickPosition(e)
      ));
    }

    // Galerie / Zoom / Video
    var media = el.closest(SEL.media) || target.closest(SEL.media);
    if (media) {
      var mediaIndex = null;
      try {
        var allMedia = document.querySelectorAll(SEL.media);
        for (var m = 0; m < allMedia.length; m++) {
          if (allMedia[m] === media) { mediaIndex = m + 1; break; }
        }
      } catch (err) { /* ungültiger Selektor */ }

      send("media_viewed", Object.assign({
        target_label: resolveTargetLabel(media) || "Medium",
        payload: {
          media_kind: media.querySelector("video, iframe") ? "video" : "bild",
          media_index: mediaIndex
        }
      }, productContext()));
    }

    // Accordion / Klappabschnitt. `toggle` blubbert nicht, deshalb am Klick:
    // zum Klickzeitpunkt ist `open` noch der ALTE Zustand — false heißt also
    // "klappt gerade auf".
    var summary = el.closest("summary");
    if (summary) {
      var details = summary.parentElement;
      if (details && details.tagName === "DETAILS" && !details.open) {
        send("section_opened", Object.assign({
          target_label: textOf(summary, 80),
          payload: { section: textOf(summary, 200) || "(ohne Titel)" }
        }, productContext()));
      }
    }

    // Popup weggeklickt
    if (popupSeen && el.closest(SEL.popupClose) && el.closest(SEL.popup)) {
      send("newsletter_dismissed", { target_label: resolveTargetLabel(el) });
    }

    send("click", Object.assign({
      target_label: resolveTargetLabel(el),
      target_selector: resolveSelector(el)
    }, clickPosition(e)));

    // Erst hier, damit der Klick selbst noch mit im Beacon steckt.
    if (leavesPage) flush(true);
  }

  // ─────────────────────────────────────────────────────────────
  // Formularinteraktionen: Variante, Menge, Filter, Sortierung
  // ─────────────────────────────────────────────────────────────

  /** Optionsname aus Shopify-üblichen Strukturen (Dawn & Ableger). */
  function optionNameOf(input) {
    if (input.name && !/^option\d?$/i.test(input.name)) return input.name.slice(0, 80);
    var fieldset = input.closest("fieldset");
    var legend = fieldset && fieldset.querySelector("legend");
    if (legend) return textOf(legend, 80);
    var wrapper = input.closest(".product-form__input, variant-selects, variant-radios");
    var label = wrapper && wrapper.querySelector("label, .form__label");
    return (label && textOf(label, 80)) || "Option";
  }

  function optionValueOf(input) {
    if (input.tagName === "SELECT") {
      var opt = input.options[input.selectedIndex];
      return (opt && (opt.textContent || opt.value) || "").trim().slice(0, 80) || null;
    }
    if (input.type === "radio" || input.type === "checkbox") {
      // CSS.escape fehlt in älteren Browsern — dann eben ohne Label-Lookup.
      var byId = null;
      if (input.id && window.CSS && typeof CSS.escape === "function") {
        byId = document.querySelector('label[for="' + CSS.escape(input.id) + '"]');
      }
      var wrapped = input.closest("label");
      return (byId && textOf(byId, 80)) ||
             (wrapped && textOf(wrapped, 80)) ||
             (input.value || "").slice(0, 80) || null;
    }
    return (input.value || "").slice(0, 80) || null;
  }

  function onChangeCapture(e) {
    var input = e.target;
    if (!input || !input.tagName || !input.closest) return;
    if (input.type === "password" || input.type === "email" || input.type === "tel") return;

    // Sortierung
    if (input.matches(SEL.sortSelect)) {
      send("sort_changed", {
        target_label: optionValueOf(input),
        payload: { sort_value: (input.value || "").slice(0, 80) }
      });
      return;
    }

    // Facettenfilter
    if (input.closest(SEL.filterScope)) {
      // Abgewählte Checkbox ist kein gesetzter Filter.
      if ((input.type === "checkbox" || input.type === "radio") && !input.checked) return;
      var facetName = input.name || "";
      // Shopify-Filternamen sehen aus wie "filter.v.option.color" — für die
      // Auswertung reicht der letzte Abschnitt.
      var pretty = facetName.split(".").pop() || facetName || "(Filter)";
      send("filter_applied", {
        target_label: optionValueOf(input),
        payload: {
          filter_name: pretty.slice(0, 80),
          filter_value: (optionValueOf(input) || "").slice(0, 80)
        }
      });
      return;
    }

    // Menge
    if (input.matches(SEL.quantityInput)) {
      send("quantity_changed", Object.assign({
        quantity: parseInt(input.value, 10) || null,
        payload: { to: parseInt(input.value, 10) || 0 }
      }, productContext()));
      return;
    }

    // Variante / Produktoption
    if (input.matches(SEL.variantInput)) {
      if ((input.type === "radio" || input.type === "checkbox") && !input.checked) return;
      send("variant_selected", Object.assign({
        target_label: optionValueOf(input),
        payload: {
          option_name: optionNameOf(input) || "Option",
          option_value: optionValueOf(input) || "–"
        }
      }, productContext()));
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Popup / Newsletter
  // ─────────────────────────────────────────────────────────────
  // Heuristisch und damit theme-abhängig: welches Element ein "Popup" ist,
  // steht nirgends geschrieben. Die Selektoren sind deshalb überschreibbar,
  // und der Beobachter gibt nach einer Minute auf — Popups, die später als
  // das kommen, gibt es praktisch nicht.

  var popupSeen = false;
  var popupObserver = null;

  /**
   * Ein Popup ist sichtbar UND liegt über der Seite. Die Overlay-Prüfung ist
   * der entscheidende Teil: fast jedes Shopify-Theme hat ein
   * Newsletter-Formular im Footer, und das würde sonst auf jeder einzelnen
   * Seite als "Popup eingeblendet" gezählt.
   */
  function isVisiblePopup(el) {
    if (!el || !el.getBoundingClientRect) return false;
    var r = el.getBoundingClientRect();
    if (r.width < 80 || r.height < 80) return false;
    // Außerhalb des Viewports = nicht eingeblendet.
    if (r.bottom < 0 || r.top > window.innerHeight) return false;
    try {
      var cs = window.getComputedStyle(el);
      if (cs.display === "none" || cs.visibility === "hidden" || cs.opacity === "0") return false;
      return el.tagName === "DIALOG" || cs.position === "fixed";
    } catch (e) {
      return false;
    }
  }

  function scanForPopup() {
    if (popupSeen) return;
    var candidates;
    try {
      candidates = document.querySelectorAll(SEL.popup);
    } catch (e) {
      return;
    }
    for (var i = 0; i < candidates.length; i++) {
      if (isVisiblePopup(candidates[i])) {
        popupSeen = true;
        send("newsletter_shown", {
          target_label: textOf(candidates[i].querySelector("h1, h2, h3, .h2"), 80) || "Popup",
          payload: { popup_id: (candidates[i].id || candidates[i].className || "popup").slice(0, 80) }
        });
        stopPopupWatch();
        return;
      }
    }
  }

  function stopPopupWatch() {
    if (popupObserver) {
      popupObserver.disconnect();
      popupObserver = null;
    }
  }

  function watchPopup() {
    scanForPopup();
    if (popupSeen || typeof MutationObserver !== "function") return;

    var scheduled = false;
    popupObserver = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      // Entprellen: ein Popup, das sich einblendet, erzeugt eine ganze Salve
      // von Mutationen — geprüft wird einmal danach, nicht bei jeder.
      setTimeout(safe(function () {
        scheduled = false;
        scanForPopup();
      }), 400);
    });
    popupObserver.observe(document.body, { childList: true, subtree: true });
    setTimeout(stopPopupWatch, 60000);
  }

  function onSubmitCapture(e) {
    var form = e.target;
    if (!form || !form.querySelector) return;
    var isNewsletter =
      (form.closest && form.closest(SEL.popup)) ||
      /contact|newsletter|subscribe/i.test(form.getAttribute("action") || "");
    if (!isNewsletter) return;
    if (!form.querySelector('input[type="email"]')) return;

    // Bewusst ohne jeden Feldinhalt — die E-Mail-Adresse gehört Shopify, nicht
    // dieser Datenbank (DSGVO, siehe PLAN.md).
    send("newsletter_submitted", {
      payload: { popup_id: (form.id || form.className || "form").slice(0, 80) }
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Exit-Intent
  // ─────────────────────────────────────────────────────────────
  // Nur Desktop: auf Touch-Geräten gibt es keinen Mauszeiger, der das Fenster
  // nach oben verlässt.

  var exitFired = false;
  var pageEnteredAt = Date.now();

  function onMouseOut(e) {
    if (exitFired) return;
    if (e.clientY > 0 || e.relatedTarget) return;
    exitFired = true;
    send("exit_intent", {
      payload: { seconds_on_page: Math.round((Date.now() - pageEnteredAt) / 1000) }
    });
  }

  // ─────────────────────────────────────────────────────────────
  // JS-Fehler
  // ─────────────────────────────────────────────────────────────
  // Gedeckelt, weil eine Fehlerschleife im Theme sonst die Datenbank flutet.
  // Die Meldung wird gekürzt und von Query-Strings befreit — dort landen
  // gelegentlich Tokens.

  var MAX_ERRORS_PER_PAGE = 3;
  var errorsSent = 0;

  function stripQuery(url) {
    if (!url) return null;
    return String(url).split("?")[0].slice(0, 200);
  }

  function reportError(message, source, line) {
    if (errorsSent >= MAX_ERRORS_PER_PAGE) return;
    errorsSent++;
    send("js_error", {
      payload: {
        message: String(message || "unbekannt").slice(0, 200),
        source_file: stripQuery(source),
        line: typeof line === "number" ? line : 0
      }
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Web Vitals
  // ─────────────────────────────────────────────────────────────
  // Bewusst ohne die web-vitals-Bibliothek: eine zusätzliche Abhängigkeit im
  // Theme wiegt schwerer als die letzten Prozente Genauigkeit.
  // INP ist hier die längste beobachtete Interaktionsdauer — die offizielle
  // Definition nimmt das 98. Perzentil aller Interaktionen. Für "reagiert der
  // Shop träge?" ist der Maximalwert die schärfere Frage.

  var vitals = { lcp: 0, cls: 0, inp: 0, ttfb: 0, fcp: 0 };
  var vitalsSent = false;

  function observeVitals() {
    if (typeof PerformanceObserver !== "function") return;

    function obs(type, cb, extra) {
      try {
        var o = new PerformanceObserver(safe(function (list) {
          list.getEntries().forEach(cb);
        }));
        o.observe(Object.assign({ type: type, buffered: true }, extra || {}));
      } catch (e) {
        /* Entry-Typ in diesem Browser nicht verfügbar */
      }
    }

    obs("largest-contentful-paint", function (entry) {
      vitals.lcp = Math.round(entry.startTime);
    });
    obs("layout-shift", function (entry) {
      if (!entry.hadRecentInput) vitals.cls += entry.value;
    });
    obs("event", function (entry) {
      if (entry.duration > vitals.inp) vitals.inp = Math.round(entry.duration);
    }, { durationThreshold: 40 });

    try {
      var nav = performance.getEntriesByType("navigation")[0];
      if (nav) vitals.ttfb = Math.round(nav.responseStart);
      var fcp = performance.getEntriesByName("first-contentful-paint")[0];
      if (fcp) vitals.fcp = Math.round(fcp.startTime);
    } catch (e) {
      /* Navigation-Timing nicht verfügbar */
    }
  }

  function flushVitals() {
    if (vitalsSent) return;
    if (!vitals.lcp && !vitals.fcp && !vitals.ttfb) return;
    vitalsSent = true;
    send("web_vitals", {
      payload: {
        lcp: vitals.lcp,
        cls: Math.round(vitals.cls * 1000) / 1000,
        inp: vitals.inp,
        ttfb: vitals.ttfb,
        fcp: vitals.fcp
      }
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Seitenwechsel ohne Neuladen
  // ─────────────────────────────────────────────────────────────
  // Facettenfilter und Paginierung in modernen Themes tauschen nur einen
  // Abschnitt aus und schreiben die URL per history.replaceState um. Ohne
  // diesen Block liefe die ganze Sitzung als ein einziger Seitenaufruf, und
  // Scroll-Tiefe wie Aufmerksamkeit würden über mehrere Seiten vermischt.

  var lastPath = location.pathname;

  function onUrlChange() {
    if (location.pathname === lastPath) return;
    lastPath = location.pathname;

    flushDwell();
    flushScroll();
    flushAttention();
    flush(false);

    scrollStartedAt = Date.now();
    pageEnteredAt = Date.now();
    maxScrollPct = 0;
    exitFired = false;
    eventsSent = 0;

    send("page_view");
  }

  function watchHistory() {
    ["pushState", "replaceState"].forEach(function (method) {
      try {
        var original = history[method];
        if (typeof original !== "function") return;
        history[method] = function () {
          var result = original.apply(this, arguments);
          setTimeout(safe(onUrlChange), 0);
          return result;
        };
      } catch (e) {
        /* History-API gesperrt */
      }
    });
    on(window, "popstate", onUrlChange);
  }

  // ─────────────────────────────────────────────────────────────
  // Start nach Consent
  // ─────────────────────────────────────────────────────────────

  function flushAll(useBeacon) {
    flushDwell();
    flushScroll();
    flushAttention();
    flushVitals();
    flush(useBeacon);
  }

  function startTracking() {
    if (consentGiven) return;
    consentGiven = true;
    sessionId = getSessionId();
    visitorId = getVisitorId();

    // Die Uhr läuft ab Consent, nicht ab Skript-Start: die Zeit bis zum Klick
    // im Cookie-Banner ist keine gemessene Verweildauer.
    lastTickAt = Date.now();
    lastActivityAt = lastTickAt;
    wasVisible = document.visibilityState === "visible";

    send("page_view");

    // Aktivität
    on(document, "mousemove", markActivity, { passive: true });
    on(document, "keydown", markActivity, { passive: true });
    on(document, "touchstart", markActivity, { passive: true });
    on(document, "scroll", function () {
      markActivity();
      trackScrollPosition();
    }, { passive: true });

    // Interaktionen
    on(document, "click", onClickCapture, true);
    on(document, "change", onChangeCapture, true);
    on(document, "submit", onSubmitCapture, true);
    if (detectDevice() === "desktop") on(document, "mouseout", onMouseOut);

    // Fehler
    on(window, "error", function (e) {
      reportError(e.message, e.filename, e.lineno);
    });
    on(window, "unhandledrejection", function (e) {
      var reason = e.reason;
      reportError(reason && reason.message ? reason.message : reason, null, 0);
    });

    observeVitals();
    watchPopup();
    watchHistory();

    heartbeatInterval = setInterval(safe(heartbeat), HEARTBEAT_MS);
    attentionInterval = setInterval(safe(attentionTick), ATTENTION_TICK_MS);
    flushTimer = setInterval(safe(function () { flush(false); }), FLUSH_INTERVAL_MS);

    on(document, "visibilitychange", function () {
      // Erst die abgelaufene Zeit dem alten Sichtbarkeitszustand zuschlagen,
      // dann umschalten — sonst zählt die letzte sichtbare Spanne nicht.
      accumulate();
      wasVisible = document.visibilityState === "visible";
      if (!wasVisible) flushAll(true);
    });
    // Safari feuert visibilitychange beim Schließen nicht zuverlässig.
    on(window, "pagehide", function () { flushAll(true); });
  }

  /**
   * Die Customer Privacy API ist NICHT von sich aus auf der Seite — sie muss
   * über Shopify.loadFeatures nachgeladen werden. Ohne diesen Schritt ist
   * window.Shopify.customerPrivacy dauerhaft undefined, hasAnalyticsConsent()
   * liefert immer false und der Tracker feuert nie ein einziges Event.
   * (Genau dieser Fehler hat das Tracking anfangs komplett stillgelegt.)
   */
  function withPrivacyApi(cb) {
    if (window.Shopify && window.Shopify.customerPrivacy) {
      cb();
      return;
    }
    if (!window.Shopify || !window.Shopify.loadFeatures) return;
    window.Shopify.loadFeatures(
      [{ name: "consent-tracking-api", version: "0.1" }],
      function (err) {
        if (!err) cb();
      }
    );
  }

  function init() {
    withPrivacyApi(function () {
      if (hasAnalyticsConsent()) startTracking();

      // Erst nach dem Laden registrieren: vorher gäbe es keine API, gegen die
      // der Handler seine Consent-Prüfung ausführen könnte.
      on(document, "visitorConsentCollected", function () {
        if (hasAnalyticsConsent()) startTracking();
      });
    });
  }

  if (document.readyState === "loading") {
    on(document, "DOMContentLoaded", init);
  } else {
    init();
  }
})();
